
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/useAuth';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotify } from '@/hooks/useNotify';

export default function AdminSettings({ isSuperuser }: { isSuperuser: boolean }) {
  const { user } = useAuth();
  const notify = useNotify();
  const [showAdminInfo, setShowAdminInfo] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isImpersonatingUser = user?.impersonated === true;
  
  // Debug function to test database connections with separate queries
  const testDatabaseQueries = async () => {
    setIsLoading(true);
    try {
      // Test admin users query
      const { data: admins, error: adminsError } = await supabase
        .from('admin_users')
        .select('*');

      if (adminsError) {
        console.error('Admin users query error:', adminsError);
        notify.error(adminsError);
      }

      // Get admin user IDs
      const adminIds = admins?.map(admin => admin.id) || [];

      // Test profiles query for admin users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', adminIds);
      
      if (profilesError) {
        console.error('Profiles query error:', profilesError);
        notify.error(profilesError);
      }

      // Combine the data
      const combinedData = admins?.map(admin => ({
        ...admin,
        profile: profiles?.find(profile => profile.id === admin.id)
      }));
      
      setDebugInfo({
        admins: { count: admins?.length || 0, error: adminsError?.message },
        profiles: { 
          count: profiles?.length || 0, 
          error: profilesError?.message,
          matchedProfiles: profiles?.length || 0
        },
        combined: {
          count: combinedData?.length || 0,
          data: combinedData
        }
      });

      if (!adminsError && !profilesError) {
        notify.success(`Found ${admins?.length || 0} admin users and ${profiles?.length || 0} matching profiles`);
      }

    } catch (error) {
      console.error('Test query error:', error);
      setDebugInfo({ error: String(error) });
      notify.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Admin Settings</CardTitle>
          <CardDescription>Configure admin-specific settings</CardDescription>
        </CardHeader>
        <CardContent>
          {isSuperuser ? (
            <div className="space-y-4">
              <p>As a superuser, you have access to all settings.</p>
              
              {isImpersonatingUser && (
                <div className="p-4 border rounded-md bg-amber-50 border-amber-200">
                  <p className="text-amber-800 font-medium mb-2">
                    You are currently impersonating a user.
                  </p>
                  <p className="text-sm text-amber-700">
                    Some admin functionality may be limited while impersonation is active.
                  </p>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => setShowAdminInfo(!showAdminInfo)}
              >
                {showAdminInfo ? "Hide" : "Show"} Admin Information
              </Button>
              
              {showAdminInfo && (
                <div className="mt-4 p-4 border rounded-md bg-slate-50">
                  <p className="font-medium mb-2">User Information:</p>
                  <pre className="text-xs bg-slate-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify({ 
                      userId: user?.id,
                      impersonated: user?.impersonated || false
                    }, null, 2)}
                  </pre>
                </div>
              )}
              
              <div className="border-t pt-4 mt-6">
                <h3 className="font-medium mb-2">Database Connection Test</h3>
                <Button 
                  onClick={testDatabaseQueries} 
                  variant="secondary" 
                  size="sm"
                  disabled={isLoading}
                >
                  {isLoading ? "Testing..." : "Test Database Queries"}
                </Button>
                
                {debugInfo && (
                  <div className="mt-4 p-4 border rounded-md bg-slate-50">
                    <p className="font-medium mb-2">Debug Information:</p>
                    <pre className="text-xs bg-slate-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(debugInfo, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p>Only superusers can modify these settings.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
